/**
 * MULTI-JURISDICTION P6 — jurisdiction-aware formatting layer (frontend-client).
 *
 * The app historically hard-coded Québec/Canada assumptions in ~400 call-sites
 * (`toLocaleString('fr-CA')`, a literal `$`, `CAD`, TPS/TVQ). This module makes
 * currency / number / date formatting and domain terminology resolve from the
 * TENANT'S jurisdiction (via the framework-service registry/resolver) instead.
 *
 * Design goals:
 *  - ZERO regression: with no resolved config the module falls back to the exact
 *    current behaviour (CAD / fr-CA), so a Québec tenant renders identically.
 *  - Hook-free access: the pure `formatCurrency/formatNumber/formatDate/term`
 *    helpers read a module-level singleton set by `JurisdictionProvider`, so the
 *    hundreds of non-component call-sites can migrate with a one-line import
 *    swap and NO refactor to hooks.
 *  - A US / EU / any tenant automatically gets its own currency + locale as soon
 *    as its jurisdiction pack is loaded — no code change (the goal's whole point).
 */

export interface JurisdictionConfig {
  /** Full assigned closure, most-specific first, e.g. ['US-CA','US']. */
  jurisdictions: string[]
  /** The tenant's home jurisdiction — drives currency/locale/terminology. */
  primary: string
  /** ISO-4217 currency code, e.g. 'CAD' | 'USD' | 'EUR'. */
  currency: string
  /** BCP-47 locale, e.g. 'fr-CA' | 'en-US' | 'en-GB'. */
  locale: string
  /** Optional server-provided display hints. */
  dateFormat?: string
  numberFormat?: string
  /** Region-specific vocabulary overrides (e.g. EFVP↔PIA, TPS↔GST). */
  terminology: Record<string, string>
  /** Applicable regulatory bodies (CAI/AMF/FTC/CPPA/…) for badges/labels. */
  bodies: Array<{ code: string; name?: string }>
}

/**
 * The current-behaviour fallback. Identical to what the app renders today for a
 * Québec tenant, so an unresolved config is a strict no-op.
 */
export const FALLBACK_JURISDICTION: JurisdictionConfig = {
  jurisdictions: ['CA-QC', 'CA'],
  primary: 'CA-QC',
  currency: 'CAD',
  locale: 'fr-CA',
  terminology: {},
  bodies: [],
}

let _active: JurisdictionConfig = FALLBACK_JURISDICTION

/** Set by JurisdictionProvider once the tenant's config resolves. */
export function setActiveJurisdiction(config: JurisdictionConfig): void {
  _active = config || FALLBACK_JURISDICTION
}

/** Read the active jurisdiction config (falls back to CA-QC). */
export function getActiveJurisdiction(): JurisdictionConfig {
  return _active
}

type CurrencyOpts = {
  config?: JurisdictionConfig
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  notation?: Intl.NumberFormatOptions['notation']
}

/**
 * Format a monetary amount in the active jurisdiction's currency + locale.
 * Replaces `${'$'}${n.toLocaleString()}` / `${n.toLocaleString('fr-CA')} $`.
 */
export function formatCurrency(amount: number | string | null | undefined, opts: CurrencyOpts = {}): string {
  const cfg = opts.config ?? _active
  const value = typeof amount === 'string' ? parseFloat(amount) : amount
  const n = Number.isFinite(value as number) ? (value as number) : 0
  try {
    return new Intl.NumberFormat(cfg.locale, {
      style: 'currency',
      currency: cfg.currency,
      minimumFractionDigits: opts.minimumFractionDigits,
      maximumFractionDigits: opts.maximumFractionDigits,
      notation: opts.notation,
    }).format(n)
  } catch {
    // Unknown currency/locale (never in prod) → plain grouped number.
    return `${n.toLocaleString()}`
  }
}

/** Format a plain number in the active locale. */
export function formatNumber(
  amount: number | string | null | undefined,
  opts: { config?: JurisdictionConfig } & Intl.NumberFormatOptions = {}
): string {
  const { config, ...rest } = opts
  const cfg = config ?? _active
  const value = typeof amount === 'string' ? parseFloat(amount) : amount
  const n = Number.isFinite(value as number) ? (value as number) : 0
  try {
    return new Intl.NumberFormat(cfg.locale, rest).format(n)
  } catch {
    return `${n.toLocaleString()}`
  }
}

/** Format a date/timestamp in the active locale. */
export function formatDate(
  value: string | number | Date | null | undefined,
  opts: { config?: JurisdictionConfig } & Intl.DateTimeFormatOptions = {}
): string {
  if (value === null || value === undefined || value === '') return ''
  const { config, ...rest } = opts
  const cfg = config ?? _active
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  try {
    return new Intl.DateTimeFormat(cfg.locale, rest).format(d)
  } catch {
    return d.toLocaleString()
  }
}

/**
 * Resolve a jurisdiction-specific term. Falls back to the supplied default (the
 * current hard-coded French label), so unmapped terms are unchanged.
 * e.g. term('privacy_impact_assessment', 'EFVP')  → 'EFVP' (QC) | 'PIA' (US).
 */
export function term(key: string, fallback: string): string {
  const t = _active.terminology?.[key]
  return t && t.trim() ? t : fallback
}

/** The active currency symbol (for compact UI where the full format is too wide). */
export function currencySymbol(config?: JurisdictionConfig): string {
  const cfg = config ?? _active
  try {
    const parts = new Intl.NumberFormat(cfg.locale, { style: 'currency', currency: cfg.currency }).formatToParts(0)
    return parts.find((p) => p.type === 'currency')?.value ?? '$'
  } catch {
    return '$'
  }
}
